import {
  type CreateTableOverlayConfig,
  createTableOverlay,
} from "@/components/discovery/table-overlays/createTableOverlay";
import useDiscoveryContentManagementStore from "@/store/discovery/discovery-content-management/discovery-content-management.slice";
import useNewsFeedParamsStore from "@/store/news-feed/params/newsFeed-params.slice";

/**
 * Row data shape for the narrative clusters table (AG Grid column fields).
 * Maps from {@link GetNarrativeClustersTableRow} when building the datasource.
 */
export type NarrativeClusterRowData = {
  id: string;
  /** Story name / cluster title */
  TITLE: string;
  /** stats.totalMentions (Union) */
  TOTAL_MENTIONS: number | string | null;
  /** stats.netImpactScore */
  NET_IMPACT_SCORE: number | null;
  /** stats.averageArticleImpact */
  AVERAGE_ARTICLE_IMPACT: number | null;
  /** stats.averageImpactStrength */
  AVERAGE_IMPACT_STRENGTH: number | null;
  /** stats.readership (Union) */
  READERSHIP: number | string | null;
  /** stats.socialEngagement (Union) */
  SOCIAL_ENGAGEMENTS: number | string | null;
  /** stats.netSentiment (ArticleSentimentPolarity) */
  NET_SENTIMENT: string | null;
  /** stats.grossImpact */
  GROSS_IMPACT: number | null;
  /** stats.grossPositiveImpact */
  GROSS_POSITIVE_IMPACT: number | null;
  /** stats.grossNeutralImpact */
  GROSS_NEUTRAL_IMPACT: number | null;
  /** stats.grossNegativeImpact */
  GROSS_NEGATIVE_IMPACT: number | null;
  /** stats.tier1 */
  TIER_1: number | null;
  /** stats.tier2 */
  TIER_2: number | null;
  /** stats.tier3 */
  TIER_3: number | null;
  /** stats.headlines */
  HEADLINES: number | null;
  /** stats.features */
  FEATURES: number | null;
  /** stats.passingMentions */
  PASSING_MENTIONS: number | null;
  /** Story volume (article count) */
  VOLUME: number;
  /** Story aggregatedImpact (for sorting/display) */
  AGGREGATED_IMPACT: number;
};

const useNarrativeClustersTableState: CreateTableOverlayConfig["useTableState"] =
  () => {
    const error = useDiscoveryContentManagementStore(
      (state) => state.narrativeClustersTableError,
    );
    const isFetching = useDiscoveryContentManagementStore(
      (state) => state.isFetchingNarrativeClustersTableData,
    );
    const totalCount = useDiscoveryContentManagementStore(
      (state) => state.totalNarrativeClustersCount,
    );
    const isInitialLoading = useDiscoveryContentManagementStore(
      (state) => state.isNarrativeClustersInitialLoading,
    );
    return { error, isFetching, totalCount, isInitialLoading };
  };

const useNarrativeClustersNoRowsActions: CreateTableOverlayConfig["useNoRowsActions"] =
  () => {
    const selectedFilters = useNewsFeedParamsStore(
      (state) => state.selectedFilters,
    );
    const clearAllSelectedFilters = useNewsFeedParamsStore(
      (state) => state.clearAllSelectedFilters,
    );
    const searchTerm = useDiscoveryContentManagementStore(
      (state) => state.narrativeClustersSearchTerm,
    );
    const resetState = useDiscoveryContentManagementStore(
      (state) => state.resetNarrativeClustersTableState,
    );
    const isAppliedFilters = selectedFilters.size > 0 || !!searchTerm;
    const onReset = () => {
      clearAllSelectedFilters();
      resetState();
    };
    return { isAppliedFilters, onReset };
  };

const useNarrativeClustersClearError: CreateTableOverlayConfig["useClearError"] =
  () => {
    const setError = useDiscoveryContentManagementStore(
      (state) => state.setNarrativeClustersTableError,
    );
    return () => setError(null);
  };

const narrativeClustersTableOverlayConfig: CreateTableOverlayConfig = {
  useTableState: useNarrativeClustersTableState,
  noRowsMessageWithFilters: "No Narrative Clusters Found for Applied Filters",
  noRowsMessageWithoutFilters: "No Narrative Clusters Found",
  useNoRowsActions: useNarrativeClustersNoRowsActions,
  errorTitle: "Failed to Load Narrative Clusters",
  useClearError: useNarrativeClustersClearError,
};

/** Creates the unified overlay component for the narrative clusters table. */
export const createNarrativeClustersUnifiedOverlay = (onRetry?: () => void) =>
  createTableOverlay(narrativeClustersTableOverlayConfig, onRetry);
